import { c as _c } from "react/compiler-runtime";
import React, { useMemo, useRef } from 'react';
import Button from '@precis-digital/kurama/Button';
import Card from '@precis-digital/kurama/Card';
import DownloadIcon from '@precis-digital/kurama/icons/DownloadIcon';
import Grid from '@precis-digital/kurama/Grid';
import MultiSelectDropdown, { type MultiSelectDropdownItem } from '@precis-digital/kurama/MultiSelectDropdown';
import Tooltip from '@precis-digital/kurama/Tooltip';
import Typography from '@precis-digital/kurama/Typography';
import { styled } from '@precis-digital/kurama/utils';
import { useTranslation } from 'shared/translations';
import { DEFAULT_PAGE_SIZE, ReportTable } from 'shared/components/ReportTable';
import { useTable, useFilters, useSortBy, usePagination, type CellProps, type FooterProps, type Row, type ColumnInstance } from 'react-table';
import { CHANNEL, CONVERSIONS, CONVERSION_VALUE, COST_PER_ACTION, DIMENSIONS, MARKET, ROAS, SPEND, DATE, METRICS_WITH_REVERSED_COLOR_SCHEME } from 'src/home/constants';
import { makeErrorToast } from 'shared/components/Toaster';
import { DEFAULT_DATA_COLUMN_PROPERTIES } from 'shared/reactTable';
import PercentageChip from 'shared/components/PercentageChip';
import NumberSummaryTooltip from 'shared/components/NumberSummaryTooltip';
import { ExpandedNumberFormat } from 'shared/components/NumberFormat';
import { BasicCpaFooter, BasicRoasFooter } from 'shared/reactTable/components';
import { useCurrentClient } from 'shared/context/ClientContext';
import { type OriginalRow } from 'shared/reactTable/types';
import { type SummaryData } from './Summary';
import { type TransformToDetailedDataFn, type Report } from 'src/home/transformations';
import { type Metric } from 'src/home/reportTemplateParameters';
import { isBudgetMetric } from 'src/home/budgetsAndTargets';
import { formatDateToString } from 'shared/dateFns';
import { sendGTMEvent } from 'shared/googleTagManager';
import { type CurrencyCodes } from 'shared/constants/currency';
import { exportReactTableToCSV, type CSVColumn } from 'shared/utils/csvExport';
import { type HomeDetailedTableQueryState, useHomeDetailedTableQueryState } from 'src/home/hooks/useHomeDetailedTableQueryState';
export interface ReportProps<TReport extends Report, TMetric extends Metric> {
  metrics: readonly TMetric[];
  timePeriod: [string, string];
  previousTimePeriod: [string, string];
  summaryData?: SummaryData;
  metricToPreviousMetricMap: Partial<Record<TMetric, string>>;
  reports: TReport[];
  reportsForPreviousTimePeriod: TReport[];
  transformToDetailedReportData: TransformToDetailedDataFn<TReport>;
  summaryDataForPreviousTimePeriod?: SummaryData;
  tableQueryState?: HomeDetailedTableQueryState;
  ref?: React.RefObject<HTMLDivElement | null>;
}
interface ColumnWithCSV {
  id?: string;
  csvHeader?: string;
  csvAccessor?: string | ((row: Record<string, unknown>) => unknown);
  accessor?: unknown;
}
const ValueCell = t0 => {
  const $ = _c(2);
  const {
    value
  } = t0;
  let t1;
  if ($[0] !== value) {
    t1 = <Grid container={true} flexWrap="nowrap" alignItems="center" gap="8px"><Grid item={true} xs={12}><Typography variant="body2">{value}</Typography></Grid></Grid>;
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const DateCell = t0 => {
  const $ = _c(4);
  const {
    value
  } = t0;
  let t1;
  if ($[0] !== value) {
    t1 = new Date(value).toISOString().split("T");
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1[0]) {
    t2 = <Grid container={true} flexWrap="nowrap" alignItems="center" gap="8px"><Grid item={true} xs={12}><Typography variant="body2">{t1[0]}</Typography></Grid></Grid>;
    $[2] = t1[0];
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
interface MetricCellProps {
  value: number;
  row: Row<OriginalRow>;
  metric: Metric;
  metricToPreviousMetricMap: Partial<Record<Metric, string>>;
  previousTimePeriod: [string, string];
  timePeriod: [string, string];
  showPercentageChip?: boolean;
}
const MetricCell = t0 => {
  const $ = _c(19);
  const {
    value,
    row,
    metric,
    metricToPreviousMetricMap,
    previousTimePeriod,
    timePeriod,
    showPercentageChip: t1
  } = t0;
  const showPercentageChip = t1 === undefined ? true : t1;
  const {
    currentClient: t2
  } = useCurrentClient();
  const {
    currency: targetCurrencyCode
  } = t2;
  const {
    t
  } = useTranslation("home");
  const previousMetricKey = metricToPreviousMetricMap[metric];
  const previousValue = previousMetricKey == null ? 0 : row.original[previousMetricKey] as number;
  let t3;
  if ($[0] !== metric) {
    t3 = METRICS_WITH_REVERSED_COLOR_SCHEME.includes(metric);
    $[0] = metric;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const reverseColorScheme = t3;
  let t4;
  if ($[2] !== metric || $[3] !== targetCurrencyCode || $[4] !== value) {
    t4 = <ExpandedNumberFormat value={value} id={metric} currencyCode={targetCurrencyCode} />;
    $[2] = metric;
    $[3] = targetCurrencyCode;
    $[4] = value;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== metric || $[7] !== previousTimePeriod || $[8] !== previousValue || $[9] !== reverseColorScheme || $[10] !== showPercentageChip || $[11] !== t || $[12] !== targetCurrencyCode || $[13] !== timePeriod || $[14] !== value) {
    t5 = showPercentageChip && <NumberSummaryTooltip presentValue={value} previousValue={previousValue} previousTimePeriod={previousTimePeriod} timePeriod={timePeriod} currencyCode={targetCurrencyCode} id={metric} title={t(metric)} reverseColorScheme={reverseColorScheme}><PercentageChip presentValue={value} previousValue={previousValue} reverseColorScheme={reverseColorScheme} /></NumberSummaryTooltip>;
    $[6] = metric;
    $[7] = previousTimePeriod;
    $[8] = previousValue;
    $[9] = reverseColorScheme;
    $[10] = showPercentageChip;
    $[11] = t;
    $[12] = targetCurrencyCode;
    $[13] = timePeriod;
    $[14] = value;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t4 || $[17] !== t5) {
    t6 = <StyledGridCell>{t4}{t5}</StyledGridCell>;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
};
interface TableFooterProps {
  rows: Array<Row<OriginalRow>>;
  column: ColumnInstance<OriginalRow>;
  metric: Metric;
  summaryData?: SummaryData;
  summaryDataForPreviousTimePeriod?: SummaryData;
  previousTimePeriod?: [string, string];
  timePeriod?: [string, string];
}
interface MetricColumnMeta {
  metric: Metric;
  metricToPreviousMetricMap: Partial<Record<Metric, string>>;
  previousTimePeriod: [string, string];
  timePeriod: [string, string];
  summaryData?: SummaryData;
  summaryDataForPreviousTimePeriod?: SummaryData;
}
const TooltipWithPercentageChip = t0 => {
  const $ = _c(13);
  const {
    summaryData,
    summaryDataForPreviousTimePeriod,
    previousTimePeriod,
    timePeriod,
    targetCurrencyCode,
    metric
  } = t0;
  const {
    t
  } = useTranslation("home");
  let t1;
  if ($[0] !== metric) {
    t1 = METRICS_WITH_REVERSED_COLOR_SCHEME.includes(metric);
    $[0] = metric;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const reverseColorScheme = t1;
  let t2;
  if ($[2] !== metric || $[3] !== previousTimePeriod || $[4] !== reverseColorScheme || $[5] !== summaryData || $[6] !== summaryDataForPreviousTimePeriod || $[7] !== t || $[8] !== targetCurrencyCode || $[9] !== timePeriod) {
    t2 = summaryData != null && summaryDataForPreviousTimePeriod != null && summaryData[metric as keyof SummaryData] != null && summaryDataForPreviousTimePeriod[metric as keyof SummaryData] != null && previousTimePeriod != null && timePeriod != null && <NumberSummaryTooltip presentValue={summaryData[metric as keyof SummaryData]} previousValue={summaryDataForPreviousTimePeriod[metric as keyof SummaryData]} previousTimePeriod={previousTimePeriod} timePeriod={timePeriod} currencyCode={targetCurrencyCode} id={metric} title={t(metric)} reverseColorScheme={reverseColorScheme}><PercentageChip presentValue={summaryData[metric as keyof SummaryData]} previousValue={summaryDataForPreviousTimePeriod[metric as keyof SummaryData]} reverseColorScheme={reverseColorScheme} /></NumberSummaryTooltip>;
    $[2] = metric;
    $[3] = previousTimePeriod;
    $[4] = reverseColorScheme;
    $[5] = summaryData;
    $[6] = summaryDataForPreviousTimePeriod;
    $[7] = t;
    $[8] = targetCurrencyCode;
    $[9] = timePeriod;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== t2) {
    t3 = <Grid item={true}>{t2}</Grid>;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
};
const Footer = t0 => {
  const $ = _c(42);
  const {
    rows,
    column,
    metric,
    summaryData,
    summaryDataForPreviousTimePeriod,
    previousTimePeriod,
    timePeriod
  } = t0;
  const {
    currentClient: t1
  } = useCurrentClient();
  const {
    currency: targetCurrencyCode
  } = t1;
  switch (metric) {
    case ROAS:
      {
        let t2;
        if ($[0] !== column || $[1] !== rows || $[2] !== targetCurrencyCode) {
          t2 = <BasicRoasFooter rows={rows} column={column} currencyCode={targetCurrencyCode} revenueField={CONVERSION_VALUE} spendField={SPEND} />;
          $[0] = column;
          $[1] = rows;
          $[2] = targetCurrencyCode;
          $[3] = t2;
        } else {
          t2 = $[3];
        }
        let t3;
        if ($[4] !== metric || $[5] !== previousTimePeriod || $[6] !== summaryData || $[7] !== summaryDataForPreviousTimePeriod || $[8] !== targetCurrencyCode || $[9] !== timePeriod) {
          t3 = <TooltipWithPercentageChip summaryData={summaryData} summaryDataForPreviousTimePeriod={summaryDataForPreviousTimePeriod} previousTimePeriod={previousTimePeriod} timePeriod={timePeriod} targetCurrencyCode={targetCurrencyCode} metric={metric} />;
          $[4] = metric;
          $[5] = previousTimePeriod;
          $[6] = summaryData;
          $[7] = summaryDataForPreviousTimePeriod;
          $[8] = targetCurrencyCode;
          $[9] = timePeriod;
          $[10] = t3;
        } else {
          t3 = $[10];
        }
        let t4;
        if ($[11] !== t2 || $[12] !== t3) {
          t4 = <StyledGridCell>{t2}{t3}</StyledGridCell>;
          $[11] = t2;
          $[12] = t3;
          $[13] = t4;
        } else {
          t4 = $[13];
        }
        return t4;
      }
    case COST_PER_ACTION:
      {
        let t2;
        if ($[14] !== column || $[15] !== rows || $[16] !== targetCurrencyCode) {
          t2 = <BasicCpaFooter rows={rows} column={column} currencyCode={targetCurrencyCode} spendField={SPEND} conversionField={CONVERSIONS} />;
          $[14] = column;
          $[15] = rows;
          $[16] = targetCurrencyCode;
          $[17] = t2;
        } else {
          t2 = $[17];
        }
        let t3;
        if ($[18] !== metric || $[19] !== previousTimePeriod || $[20] !== summaryData || $[21] !== summaryDataForPreviousTimePeriod || $[22] !== targetCurrencyCode || $[23] !== timePeriod) {
          t3 = <TooltipWithPercentageChip summaryData={summaryData} summaryDataForPreviousTimePeriod={summaryDataForPreviousTimePeriod} previousTimePeriod={previousTimePeriod} timePeriod={timePeriod} targetCurrencyCode={targetCurrencyCode} metric={metric} />;
          $[18] = metric;
          $[19] = previousTimePeriod;
          $[20] = summaryData;
          $[21] = summaryDataForPreviousTimePeriod;
          $[22] = targetCurrencyCode;
          $[23] = timePeriod;
          $[24] = t3;
        } else {
          t3 = $[24];
        }
        let t4;
        if ($[25] !== t2 || $[26] !== t3) {
          t4 = <StyledGridCell>{t2}{t3}</StyledGridCell>;
          $[25] = t2;
          $[26] = t3;
          $[27] = t4;
        } else {
          t4 = $[27];
        }
        return t4;
      }
    default:
      {
        const t2 = summaryData?.[metric as keyof SummaryData] ?? 0;
        let t3;
        if ($[28] !== metric || $[29] !== t2 || $[30] !== targetCurrencyCode) {
          t3 = <ExpandedNumberFormat value={t2} id={metric} currencyCode={targetCurrencyCode} />;
          $[28] = metric;
          $[29] = t2;
          $[30] = targetCurrencyCode;
          $[31] = t3;
        } else {
          t3 = $[31];
        }
        let t4;
        if ($[32] !== metric || $[33] !== previousTimePeriod || $[34] !== summaryData || $[35] !== summaryDataForPreviousTimePeriod || $[36] !== targetCurrencyCode || $[37] !== timePeriod) {
          t4 = <TooltipWithPercentageChip summaryData={summaryData} summaryDataForPreviousTimePeriod={summaryDataForPreviousTimePeriod} previousTimePeriod={previousTimePeriod} timePeriod={timePeriod} targetCurrencyCode={targetCurrencyCode} metric={metric} />;
          $[32] = metric;
          $[33] = previousTimePeriod;
          $[34] = summaryData;
          $[35] = summaryDataForPreviousTimePeriod;
          $[36] = targetCurrencyCode;
          $[37] = timePeriod;
          $[38] = t4;
        } else {
          t4 = $[38];
        }
        let t5;
        if ($[39] !== t3 || $[40] !== t4) {
          t5 = <StyledGridCell>{t3}{t4}</StyledGridCell>;
          $[39] = t3;
          $[40] = t4;
          $[41] = t5;
        } else {
          t5 = $[41];
        }
        return t5;
      }
  }
};
const MetricCellColumn = t0 => {
  const $ = _c(10);
  const {
    value,
    row,
    column
  } = t0;
  const meta = column as unknown as ColumnInstance<OriginalRow> & MetricColumnMeta;
  const t1 = meta.metric;
  const t2 = meta.metricToPreviousMetricMap;
  const t3 = meta.previousTimePeriod;
  const t4 = meta.timePeriod;
  let t5;
  if ($[0] !== meta.metric) {
    t5 = isBudgetMetric(meta.metric);
    $[0] = meta.metric;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const t6 = !t5;
  let t7;
  if ($[2] !== meta.metric || $[3] !== meta.metricToPreviousMetricMap || $[4] !== meta.previousTimePeriod || $[5] !== meta.timePeriod || $[6] !== row || $[7] !== t6 || $[8] !== value) {
    t7 = <MetricCell value={value} row={row} metric={t1} metricToPreviousMetricMap={t2} previousTimePeriod={t3} timePeriod={t4} showPercentageChip={t6} />;
    $[2] = meta.metric;
    $[3] = meta.metricToPreviousMetricMap;
    $[4] = meta.previousTimePeriod;
    $[5] = meta.timePeriod;
    $[6] = row;
    $[7] = t6;
    $[8] = value;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  return t7;
};
const MetricFooterColumn = t0 => {
  const $ = _c(8);
  const {
    rows,
    column
  } = t0;
  const meta = column as unknown as ColumnInstance<OriginalRow> & MetricColumnMeta;
  let t1;
  if ($[0] !== column || $[1] !== meta.metric || $[2] !== meta.previousTimePeriod || $[3] !== meta.summaryData || $[4] !== meta.summaryDataForPreviousTimePeriod || $[5] !== meta.timePeriod || $[6] !== rows) {
    t1 = <Footer metric={meta.metric} summaryData={meta.summaryData} column={column} rows={rows} summaryDataForPreviousTimePeriod={meta.summaryDataForPreviousTimePeriod} previousTimePeriod={meta.previousTimePeriod} timePeriod={meta.timePeriod} />;
    $[0] = column;
    $[1] = meta.metric;
    $[2] = meta.previousTimePeriod;
    $[3] = meta.summaryData;
    $[4] = meta.summaryDataForPreviousTimePeriod;
    $[5] = meta.timePeriod;
    $[6] = rows;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
};
const DetailedReport = <TReport extends Report, TMetric extends Metric>({
  metrics,
  reports,
  reportsForPreviousTimePeriod,
  summaryData,
  previousTimePeriod,
  timePeriod,
  metricToPreviousMetricMap,
  transformToDetailedReportData,
  summaryDataForPreviousTimePeriod,
  tableQueryState: tableQueryStateProp,
  ref: refProp
}: ReportProps<TReport, TMetric>): React.ReactElement => {
  const {
    t
  } = useTranslation('home');
  const internalRef = useRef<HTMLDivElement | null>(null);
  const internalTableQueryState = useHomeDetailedTableQueryState([...metrics]);
  const tableQueryState = tableQueryStateProp ?? internalTableQueryState;
  const ref = refProp ?? internalRef;
  const {
    selectedOptions,
    dimensions: selectedDimensions,
    setSelectedOptions
  } = tableQueryState;
  const data = useMemo(() => {
    return transformToDetailedReportData({
      reports,
      reportsForPreviousTimePeriod,
      selectedDimensions
    });
  }, [reports, reportsForPreviousTimePeriod, selectedDimensions, transformToDetailedReportData]);
  const metricOptions = metrics.map(metric => ({
    label: t(metric),
    value: metric,
    subCategory: t('filter.metrics')
  }));
  const dimensionOptions = DIMENSIONS.map(dimension => ({
    label: t(`filter.${dimension}`),
    value: dimension,
    subCategory: t('filter.dimensions')
  }));
  const columns = React.useMemo(() => {
    return [{
      ...DEFAULT_DATA_COLUMN_PROPERTIES,
      Header: t('dates'),
      id: DATE,
      accessor: DATE,
      csvHeader: t('dates'),
      csvAccessor: DATE,
      Cell: DateCell,
      sortType: (rowA: Row<OriginalRow>, rowB: Row<OriginalRow>) => {
        const a = new Date(rowA.values[DATE]).getTime();
        const b = new Date(rowB.values[DATE]).getTime();
        return a - b;
      }
    }, {
      ...DEFAULT_DATA_COLUMN_PROPERTIES,
      Header: t('channels'),
      id: CHANNEL,
      accessor: CHANNEL,
      csvHeader: t('channels'),
      csvAccessor: CHANNEL,
      Cell: ValueCell
    }, {
      ...DEFAULT_DATA_COLUMN_PROPERTIES,
      Header: t('markets'),
      id: MARKET,
      accessor: MARKET,
      csvHeader: t('markets'),
      csvAccessor: MARKET,
      Cell: ValueCell
    }, ...metrics.map(metric_0 => {
      return {
        ...DEFAULT_DATA_COLUMN_PROPERTIES,
        Header: t(metric_0),
        id: metric_0,
        accessor: metric_0,
        csvHeader: t(metric_0),
        csvAccessor: metric_0,
        metric: metric_0,
        metricToPreviousMetricMap,
        previousTimePeriod,
        timePeriod,
        summaryData,
        summaryDataForPreviousTimePeriod,
        Cell: MetricCellColumn,
        Footer: MetricFooterColumn
      };
    })].filter(column => {
      return selectedOptions.includes(column.accessor);
    });
  }, [t, metrics, metricToPreviousMetricMap, previousTimePeriod, timePeriod, summaryData, selectedOptions, summaryDataForPreviousTimePeriod]);
  const handleExport = (): void => {
    const exportColumns = columns.map(column_0 => {
      const columnWithCSV = column_0 as ColumnWithCSV;
      return {
        id: column_0.id ?? '',
        csvHeader: columnWithCSV.csvHeader ?? column_0.id ?? '',
        accessor: (columnWithCSV.csvAccessor ?? column_0.accessor) as CSVColumn['accessor']
      };
    });
    exportReactTableToCSV({
      columns: exportColumns,
      data,
      filename: `attribution-report-${formatDateToString({
        date: new Date(timePeriod[0]),
        targetFormat: 'yyyy-MM-dd'
      })}_${formatDateToString({
        date: new Date(timePeriod[1]),
        targetFormat: 'yyyy-MM-dd'
      })}`
    });
    sendGTMEvent({
      event: 'csv_export',
      product: 'home'
    });
  };
  const handleFilterChange = (_: string | null, value: MultiSelectDropdownItem[]): void => {
    const selectedMetricsCount = metrics.filter(column_1 => value.map(metric_1 => metric_1.value).includes(column_1)).length;
    const selectedDimensionsCount = DIMENSIONS.filter(column_2 => value.map(dimension_0 => dimension_0.value).includes(column_2)).length;
    if (selectedDimensionsCount === 0) {
      makeErrorToast(t('filter.minimumOneDimensionError'));
      return;
    }
    if (selectedMetricsCount === 0) {
      makeErrorToast(t('filter.minimumOneMetricError'));
      return;
    }
    setSelectedOptions(value.map(option => option.value));
  };
  const renderFilters = (): React.ReactElement => {
    return <Grid display="flex" gap="8px" alignItems="center">
        <Typography variant="body2">{t('showMe')}</Typography>
        <MultiSelectDropdown title={t('selectColumns')} buttonTitle={t('columns')} options={[...dimensionOptions, ...metricOptions]} value={selectedOptions} allowSelectAll={false} onChange={handleFilterChange} />
        <Tooltip kind="singleline" title={t('exportTooltip')}>
          <Button disabled={data.length === 0} rightIcon={<DownloadIcon />} variant="text" onClick={handleExport}>
            {t('export')}
          </Button>
        </Tooltip>
      </Grid>;
  };
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page: pageRows,
    prepareRow,
    footerGroups,
    gotoPage,
    setPageSize,
    canNextPage,
    state: {
      pageIndex,
      pageSize
    }
  } = useTable({
    columns,
    data,
    initialState: {
      pageSize: DEFAULT_PAGE_SIZE,
      sortBy: [{
        id: selectedOptions.includes(DATE) ? DATE : metrics[0] ?? CHANNEL,
        desc: true
      }]
    }
  }, useFilters, useSortBy, usePagination);
  return <StyledBlock ref={ref}>
      <Grid container display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h3">{t('detailedReporting')}</Typography>
        {data.length > 0 && renderFilters()}
      </Grid>
      {data.length > 0 ? <ReportTable t={t} basicColumnsIds={[CHANNEL, MARKET, DATE]} tableProps={{
      styles: {
        container: {
          maxHeight: 'unset',
          margin: 0
        },
        table: {
          margin: 0
        }
      },
      getTableProps,
      getTableBodyProps,
      headerGroups,
      rows: pageRows,
      prepareRow,
      footerGroups,
      pagination: {
        isSSR: false,
        count: rows.length,
        page: pageIndex,
        handleChangePage: (__0, newPage) => {
          gotoPage(newPage);
        },
        rowsPerPage: pageSize,
        setPageSize,
        gotoPage,
        shouldDisableNextButton: !canNextPage
      }
    }} /> : <Typography style={{
      marginTop: '4px'
    }} variant="body2">
          {t('noDetailedReport')}
        </Typography>}
    </StyledBlock>;
};
const StyledBlock = styled(Card)(({
  theme
}) => ({
  borderRadius: '24px',
  width: '100%',
  backgroundColor: theme.tokens.background.main.default,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'start',
  button: {
    paddingLeft: '16px'
  },
  marginTop: 0
}));
const StyledGridCell = styled(Grid)(({
  theme
}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '8px'
}));
export default DetailedReport;